import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { enumsApi } from '../services/api';
import { showError } from '../utils/showError';
import { extractErrorMessage } from '../utils/errorUtils';
import { getRegisterTypeOptions } from '../constants/buildings';
import {
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Button,
  FormField,
  FormSelect,
  FormCheckbox,
  FormGroup,
  ErrorMessage
} from '../components/common';
import { formatEnumForSelect } from '../constants/licenses';

const CreateBuilding = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [moduleTypes, setModuleTypes] = useState([]);
  const [isLoadingModules, setIsLoadingModules] = useState(true);
  const [formData, setFormData] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    formattedAddress: '',
    latitude: 0,
    longitude: 0,
    name: '',
    userName: '',
    number: '',
    floor: '',
    isOwner: false,
    isResident: false,
    isManager: false,
    registerType: 0,
    modules: []
  });

  useEffect(() => {
    fetchAvailableModules();
  }, []);

  const fetchAvailableModules = async () => {
    setIsLoadingModules(true);
    try {
      const response = await enumsApi.getModuleTypes();
      setModuleTypes(formatEnumForSelect(response.data));
    } catch (err) {
      console.error('Error fetching modules:', err);
      setError(extractErrorMessage(err));
      setModuleTypes([]);
    } finally {
      setIsLoadingModules(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }));
  };

  const handleModuleChange = (moduleIndex) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleIndex)
        ? prev.modules.filter(m => m !== moduleIndex)
        : [...prev.modules, moduleIndex]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/Building', formData);
      navigate(`/buildings/${response.data.buildingId}`);
    } catch (err) {
      showError(err);
      console.error('Error creating building:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Create New Building"
        backLink={{ label: "Back to Buildings", href: "/buildings" }}
      />

      {error && (
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader 
            title="Building Information"
            subtitle="Enter the details for the new building"
          />
          <CardBody>
            <FormGroup title="Basic Information">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <FormField
                  label="Building Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                
                <FormField
                  label="Username"
                  name="userName"
                  value={formData.userName}
                  onChange={handleInputChange}
                />
                
                <FormSelect
                  label="Register Type"
                  name="registerType"
                  value={formData.registerType}
                  onChange={handleInputChange}
                  options={getRegisterTypeOptions()}
                />
                
                <FormField
                  label="Building Number"
                  name="number"
                  value={formData.number}
                  onChange={handleInputChange}
                />
                
                <FormField
                  label="Floor"
                  name="floor"
                  value={formData.floor}
                  onChange={handleInputChange}
                />
              </div>
            </FormGroup>

            <FormGroup title="Address" description="Enter the building address information">
              <FormField
                label="Street Address"
                name="street"
                value={formData.street}
                onChange={handleInputChange}
              />
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <FormField
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                />
                
                <FormField
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                />
                
                <FormField
                  label="Zip Code"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                />
                
                <FormField
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                />
                
                <FormField
                  label="Latitude"
                  name="latitude"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={handleInputChange}
                />
                
                <FormField
                  label="Longitude"
                  name="longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={handleInputChange}
                />
              </div>
            </FormGroup>

            <FormGroup title="Role Assignments" description="Assign roles to this building">
              <FormCheckbox
                label="Is Manager"
                name="isManager"
                checked={formData.isManager}
                onChange={handleInputChange}
              />
              <FormCheckbox
                label="Is Owner"
                name="isOwner"
                checked={formData.isOwner}
                onChange={handleInputChange}
              />
              <FormCheckbox
                label="Is Resident"
                name="isResident"
                checked={formData.isResident}
                onChange={handleInputChange}
              />
            </FormGroup>

            <FormGroup title="Building Modules" description="Select the modules available for this building">
              {isLoadingModules ? (
                <div className="text-sm text-gray-500">Loading modules...</div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {moduleTypes.map(module => (
                    <FormCheckbox
                      key={module.value}
                      label={module.label}
                      checked={formData.modules.includes(module.value)}
                      onChange={() => handleModuleChange(module.value)}
                    />
                  ))}
                </div>
              )}
            </FormGroup>
          </CardBody>
        </Card>

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            onClick={() => navigate('/buildings')}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            variant="primary"
            loading={loading}
          >
            Create Building
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateBuilding;